import React, { useState, useMemo } from 'react';
import { ArrowRight, Dices, RefreshCw, X, Link as LinkIcon, Plus, Search, Presentation as PresentationIcon } from 'lucide-react';
import { ClientEntity, Showcase, ShowcaseItem } from '../../../types';
import { toast } from 'sonner';

interface ShowcaseBuilderProps {
  clients: ClientEntity[];
  onSave: (showcase: Showcase) => void;
  onCancel: () => void;
}

export const ShowcaseBuilder: React.FC<ShowcaseBuilderProps> = ({ clients, onSave, onCancel }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  
  // Niche amounts
  const [nicheCounts, setNicheCounts] = useState<Record<string, number>>({});
  
  // Selected Items
  const [items, setItems] = useState<ShowcaseItem[]>([]);

  // External form
  const [extUrl, setExtUrl] = useState('');
  const [extTitle, setExtTitle] = useState('');
  const [extTab, setExtTab] = useState('Outros');

  // Sort and Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'volume' | 'alpha'>('volume');

  const nicheStats = useMemo(() => {
    const stats: Record<string, number> = {};
    clients.forEach(c => (c.data.categories || []).forEach(cat => {
      stats[cat] = (stats[cat] || 0) + 1;
    }));
    return Object.entries(stats).map(([name, available]) => ({ name, available }));
  }, [clients]);

  const displayedNiches = useMemo(() => {
    const filtered = nicheStats.filter(n => n.name.toLowerCase().includes(searchQuery.toLowerCase()));
    if (sortBy === 'alpha') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else {
       // sort desc by volume, then alpha
      filtered.sort((a, b) => b.available - a.available || a.name.localeCompare(b.name));
    }
    return filtered;
  }, [nicheStats, searchQuery, sortBy]);

  // Step 2 -> 3 Transition: Generate Initial Pull
  const doPull = () => {
    const newItems: ShowcaseItem[] = [];
    
    Object.entries(nicheCounts).forEach(([niche, count]) => {
      if (count > 0) {
        // Find clients of this niche
        const available = clients.filter(c => (c.data.categories || []).includes(niche));
        // Shuffle
        const shuffled = [...available].sort(() => 0.5 - Math.random());
        // Pick
        const picked = shuffled.slice(0, count);
        
        picked.forEach(client => {
          newItems.push({
            id: Date.now().toString(36) + Math.random().toString(36).substring(2),
            type: 'internal',
            clientId: client.id,
            tabCategory: niche
          });
        });
      }
    });

    setItems(newItems);
    setStep(3);
  };

  const swapItem = (itemId: string, niche: string) => {
    const currentItemsInNiche = items.filter(i => i.tabCategory === niche && i.type === 'internal').map(i => i.clientId);
    const available = clients.filter(c => (c.data.categories || []).includes(niche) && !currentItemsInNiche.includes(c.id));
    
    if (available.length === 0) {
      toast.error('Não há mais sites deste nicho para trocar. Banco esgotado.');
      return;
    }

    // eslint-disable-next-line react-hooks/purity
    const randomIndex = Math.floor(Math.random() * available.length);
    const random = available[randomIndex];
    
    setItems(prev => prev.map(i => {
      if (i.id === itemId) {
        return { ...i, clientId: random.id };
      }
      return i;
    }));
  };

  const removeItem = (itemId: string) => {
    setItems(prev => prev.filter(i => i.id !== itemId));
  };

  const handleAddExternal = () => {
    if (!extUrl || !extTitle) {
      toast.error("Preencha URL e Título"); return;
    }
    setItems(prev => [...prev, {
      id: Date.now().toString(36) + Math.random().toString(36).substring(2),
      type: 'external',
      externalUrl: extUrl.startsWith('http') ? extUrl : `https://${extUrl}`,
      externalTitle: extTitle,
      tabCategory: extTab || 'Outros'
    }]);
    setExtUrl(''); setExtTitle('');
    toast.success("Link externo injetado com sucesso!");
  };

  const finalize = () => {
    if (items.length === 0) {
       toast.error("Adicione pelo menos um item à vitrine."); return;
    }
    
    // Generate simple slug
    const slug = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, '-') + '-' + Math.floor(Math.random() * 1000);
    
    const showcase: Showcase = {
      id: Date.now().toString(36) + Math.random().toString(36).substring(2),
      slug,
      name,
      createdAt: new Date().toISOString(),
      items
    };

    onSave(showcase);
  };

  return (
    <div className="w-full max-w-4xl mx-auto py-12 px-6">
      <div className="mb-10 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black tracking-tight text-gray-900 mb-2">Engenharia de Vitrine</h2>
          <p className="text-gray-500 text-sm">Estruture as exibições exatas para a prospecção.</p>
        </div>
        <button onClick={onCancel} className="text-sm font-semibold text-gray-500 hover:text-black transition-colors">
          ✕ Cancelar
        </button>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-4 mb-10 select-none">
          <div className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${step >= 1 ? 'text-black' : 'text-gray-300'}`}><span className={`w-5 h-5 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-black text-white' : 'bg-gray-100'}`}>1</span> Batismo</div>
          <div className="w-8 h-[2px] bg-gray-100"></div>
          <div className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${step >= 2 ? 'text-black' : 'text-gray-300'}`}><span className={`w-5 h-5 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-black text-white' : 'bg-gray-100'}`}>2</span> Puxada</div>
          <div className="w-8 h-[2px] bg-gray-100"></div>
          <div className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${step >= 3 ? 'text-black' : 'text-gray-300'}`}><span className={`w-5 h-5 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-black text-white' : 'bg-gray-100'}`}>3</span> Lapidação</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 transition-all relative overflow-hidden">
        
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nomeie seu Link de Impacto</h3>
              <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Proposta Clínica Odonto Silva Premium"
                className="w-full text-base px-5 py-4 rounded-xl border border-gray-200 outline-none focus:border-black focus:ring-1 focus:ring-black transition-all mb-6 bg-gray-50 focus:bg-white"
                autoFocus
              />
              <div className="flex justify-end">
                <button 
                  onClick={() => setStep(2)}
                  disabled={!name.trim()}
                  className="bg-black text-white rounded-lg px-8 py-3.5 font-bold tracking-wide flex items-center gap-2 disabled:opacity-50 hover:bg-gray-900 transition-colors"
                >
                  Continuar <ArrowRight size={18} />
                </button>
              </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="mb-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
                 <div>
                   <h3 className="text-lg font-bold text-gray-900">Estoque de Curadoria</h3>
                   <p className="text-sm text-gray-500 mt-1">Determine, filtre e ordene as gavetas limitando com exatidão a alocação.</p>
                 </div>
                 
                 <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative w-full sm:w-48">
                       <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                       <input 
                         type="text" 
                         placeholder="Filtrar nicho..." 
                         value={searchQuery}
                         onChange={e => setSearchQuery(e.target.value)}
                         className="w-full text-sm pl-9 pr-3 py-1.5 rounded-lg border border-gray-200 outline-none focus:border-black bg-gray-50 focus:bg-white transition-colors"
                       />
                    </div>
                    <div className="flex bg-gray-100 p-1 rounded-lg shrink-0 border border-gray-200">
                       <button onClick={() => setSortBy('volume')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${sortBy === 'volume' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>Volume</button>
                       <button onClick={() => setSortBy('alpha')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${sortBy === 'alpha' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>A-Z</button>
                    </div>
                    <div className="h-8 w-px bg-gray-200 mx-1 hidden sm:block"></div>
                    <button 
                       onClick={doPull} 
                       disabled={Object.values(nicheCounts).reduce((a,b) => a+b, 0) === 0}
                       className="hidden sm:flex bg-black text-white rounded-lg px-6 py-1.5 text-sm font-bold items-center gap-2 hover:bg-gray-900 transition-colors disabled:opacity-50"
                     >
                       Avançar <ArrowRight size={14} />
                    </button>
                 </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-6 max-h-[40vh] p-1 overflow-y-auto pr-2">
                 {displayedNiches.map(({name: niche, available}) => {
                    const count = nicheCounts[niche] || 0;
                    return (
                      <div key={niche} className={`flex justify-between items-center p-2.5 rounded-lg border transition-colors ${count > 0 ? 'bg-black text-white border-black shadow-md' : 'bg-gray-50 border-gray-100 text-gray-700 hover:shadow-sm'}`}>
                         <div className="flex flex-col min-w-0 pr-2">
                           <span className="text-xs font-semibold truncate leading-tight">{niche}</span>
                           <span className={`text-[9px] font-bold mt-0.5 tracking-wide ${count > 0 ? 'text-gray-400' : 'text-indigo-500'}`}>{available} DISP.</span>
                         </div>
                         <div className="flex items-center gap-1.5 shrink-0">
                           <button onClick={() => setNicheCounts(p => ({...p, [niche]: Math.max(0, count - 1)}))} className={`w-5 h-5 text-sm rounded flex items-center justify-center font-bold transition-all ${count > 0 ? 'bg-white/20 hover:bg-white/40' : 'bg-white border border-gray-200 hover:bg-gray-100'}`}>-</button>
                           <span className="w-4 text-center text-xs font-bold">{count}</span>
                           <button 
                             disabled={count >= available} 
                             onClick={() => setNicheCounts(p => ({...p, [niche]: Math.min(available, count + 1)}))} 
                             className={`w-5 h-5 text-sm rounded flex items-center justify-center font-bold transition-all disabled:opacity-20 disabled:cursor-not-allowed ${count > 0 ? 'bg-white text-black hover:bg-gray-200' : 'bg-white border border-gray-200 hover:border-black hover:text-black'}`}
                           >+</button>
                         </div>
                      </div>
                    )
                 })}
                 {displayedNiches.length === 0 && <div className="col-span-full py-10 text-center text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl">Nenhum nicho encontrado na sua base com este termo.</div>}
              </div>

              <div className="flex justify-between sm:justify-start pt-4 items-center">
                 <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-gray-500 font-bold bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">Voltar</button>
                 <button 
                    onClick={doPull} 
                    disabled={Object.values(nicheCounts).reduce((a,b) => a+b, 0) === 0}
                    className="sm:hidden ml-auto bg-black text-white rounded-lg px-6 py-2 text-sm font-bold flex items-center gap-2 hover:bg-gray-900 transition-colors disabled:opacity-50"
                  >
                    Avançar <ArrowRight size={14} />
                 </button>
              </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="flex justify-between items-start mb-6">
               <div>
                  <h3 className="text-lg font-bold text-gray-900">A Mesa Cirúrgica</h3>
                  <p className="text-sm text-gray-500 mt-1">Refine, troque ou remova os elementos sorteados. Adicione links externos para enriquecer as abas.</p>
               </div>
            </div>

            {/* Injetor de Links Externos */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-8 flex flex-col sm:flex-row items-end gap-3">
               <div className="w-full sm:flex-1">
                 <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Título Exibição</label>
                 <input type="text" value={extTitle} onChange={e=>setExtTitle(e.target.value)} placeholder="Ex: Referência Mercado Livre" className="w-full text-sm p-2 rounded-md border border-gray-200 outline-none focus:border-black" />
               </div>
               <div className="w-full sm:flex-1">
                 <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">URL / Domínio</label>
                 <input type="text" value={extUrl} onChange={e=>setExtUrl(e.target.value)} placeholder="https://..." className="w-full text-sm p-2 rounded-md border border-gray-200 outline-none focus:border-black" />
               </div>
               <div className="w-full sm:w-1/4">
                 <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Nome da Aba</label>
                 <input type="text" value={extTab} onChange={e=>setExtTab(e.target.value)} className="w-full text-sm p-2 rounded-md border border-gray-200 outline-none focus:border-black" />
               </div>
               <button onClick={handleAddExternal} className="bg-white border border-gray-200 text-gray-700 hover:text-black hover:bg-gray-100 p-2 text-sm font-semibold rounded-md flex shrink-0 items-center justify-center gap-1 transition-colors h-[38px]"><Plus size={16}/> Ligar</button>
            </div>

            {/* Visualização dos Itens */}
            <div className="space-y-6 mb-8 max-h-[40vh] overflow-y-auto pr-2">
               {/* Agrupar por Aba */}
               {Array.from(new Set(items.map(i => i.tabCategory))).map(tab => {
                  const tabItems = items.filter(i => i.tabCategory === tab);
                  return (
                    <div key={tab} className="border border-gray-100 rounded-xl p-4">
                       <h4 className="font-black text-gray-800 uppercase tracking-widest text-xs mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-indigo-500"></span> Aba: {tab}
                       </h4>
                       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {tabItems.map(item => {
                             const client = item.type === 'internal' ? clients.find(c => c.id === item.clientId) : null;
                             return (
                               <div key={item.id} className="relative bg-white border border-gray-200 rounded-lg group hover:border-gray-300 transition-all overflow-hidden flex flex-col shadow-sm">
                                 {/* Formato Mini Site Preview Dinâmico */}
                                 <div className="h-32 relative bg-gray-50 border-b border-gray-100 overflow-hidden group-hover:opacity-100 transition-opacity">
                                    {item.type === 'internal' && client ? (
                                       <iframe 
                                          srcDoc={client.htmlContent} 
                                          className="absolute top-0 left-0 pointer-events-none bg-white border-0 z-0 select-none" 
                                          style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }} 
                                          title="preview" 
                                          tabIndex={-1} 
                                          sandbox="allow-scripts allow-same-origin allow-popups" 
                                       />
                                    ) : item.type === 'external' ? (
                                       <div className="w-full h-full relative bg-gray-50 flex flex-col items-center justify-center">
                                          <iframe 
                                             src={item.externalUrl} 
                                             className="absolute top-0 left-0 pointer-events-none bg-white border-0 z-0" 
                                             style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }} 
                                             title="external-preview"
                                             sandbox="allow-scripts allow-same-origin allow-popups"
                                          />
                                       </div>
                                    ) : (
                                       <div className="flex items-center justify-center w-full h-full text-gray-300">
                                          <LinkIcon size={32} />
                                       </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none" />
                                    
                                    {/* Overlay Action Buttons */}
                                    <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                       {item.type === 'internal' && (
                                          <button onClick={() => swapItem(item.id, tab)} className="p-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-600 hover:text-black rounded-md shadow-sm transition-colors" title="Sortear outro">
                                            <RefreshCw size={14} />
                                          </button>
                                       )}
                                       <button onClick={() => removeItem(item.id)} className="p-1.5 bg-white/90 backdrop-blur-sm border border-red-100 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 rounded-md shadow-sm transition-colors" title="Remover">
                                          <X size={14} />
                                       </button>
                                    </div>
                                 </div>

                                 {/* Info Section */}
                                 <div className="p-3 bg-white">
                                    <div className="flex items-center gap-1.5 overflow-hidden">
                                       {item.type === 'external' ? <LinkIcon size={14} className="text-gray-400 shrink-0"/> : <PresentationIcon size={14} className="text-gray-400 shrink-0" />}
                                       <span className="font-semibold text-xs tracking-tight text-gray-800 truncate">
                                          {item.type === 'internal' ? client?.data.name : item.externalTitle}
                                       </span>
                                    </div>
                                    <div className="mt-1 text-[10px] font-semibold tracking-wider text-gray-400 truncate">
                                       {item.type === 'internal' ? 'REFERÊNCIA DO BANCO' : item.externalUrl}
                                    </div>
                                 </div>
                               </div>
                             );
                          })}
                       </div>
                    </div>
                  )
               })}
               {items.length === 0 && <div className="text-center text-sm text-gray-400 py-4">Nenhum site na grade. Volte e adicione.</div>}
            </div>

            <div className="flex justify-between pt-6 border-t border-gray-100">
               <button onClick={() => setStep(2)} className="px-6 py-3 text-gray-500 font-bold bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">Modificar Quantidades</button>
               <button 
                 onClick={finalize}
                 disabled={items.length === 0}
                 className="bg-black text-white rounded-lg px-8 py-3 font-bold flex items-center gap-2 hover:bg-gray-900 transition-colors disabled:opacity-50 shadow-md"
               >
                 Embrulhar e Gerar Link
               </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
